import { NodeModel } from '@minoru/react-dnd-treeview'
import { action, computed, makeObservable, observable } from 'mobx'

import {
  IPermitType,
  IPermitTypeField,
  IWorkflowStep,
  PermitFieldType,
  WorkflowStepType,
} from '~/client/graph/types.generated'
import { getFieldTypeCaption } from '~/client/src/desktop/constants/formFieldsCaptionMap'
import {
  getDefaultAllowCreateNew,
  getDefaultFilterOptions,
  nestablePermitFields,
  workflowFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { MAX_FIELDS_DEPTH } from '~/client/src/shared/constants/permitTypeFieldsConstants'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'
import { copyObjectDeep } from '~/client/src/shared/utils/util'

import { REQUIRED_FIELD_TYPES } from './fieldTypeCategories'

export const TREE_ROOT_ID = 'tree-root-id'

export interface IImportedField {
  id: string
  caption: string
  originalType: PermitFieldType
  originalParentId: string | null
  originalStepIndex: number
  stepType: string
  fieldIndex: number
}

interface IStepInfo {
  index: number
  type: WorkflowStepType
  disabled: boolean
}

function sortTreeData(
  treeData: NodeModel<IImportedField>[],
  sortedData: NodeModel<IImportedField>[],
  parentId: string,
): void {
  const children = treeData.filter(node => node.parent === parentId)
  for (const node of children) {
    sortedData.push(node)
    sortTreeData(treeData, sortedData, node.id.toString())
  }
}

function getDepth(
  tree: NodeModel<IImportedField>[],
  id: string,
  depth = 0,
): number {
  if (depth > MAX_FIELDS_DEPTH) return depth
  const target = tree.find(node => node.id === id)
  if (target) {
    return getDepth(tree, target.parent?.toString(), depth + 1)
  }
  return depth
}

function getChildrenDepth(
  tree: NodeModel<IImportedField>[],
  ids: string[],
  depth = 0,
): number {
  if (depth > MAX_FIELDS_DEPTH) return depth
  const nestableItems = tree.filter(
    node => ids.includes(node.parent.toString()) && node.droppable,
  )
  if (nestableItems.length) {
    return getChildrenDepth(
      tree,
      nestableItems.map(({ id }) => id.toString()),
      depth + 1,
    )
  }
  return depth
}

function withResourceCreationOff(field: IPermitTypeField): IPermitTypeField {
  return getDefaultAllowCreateNew(field.type)
    ? field
    : { ...field, allowCreateNew: false, allowSelectExisting: true }
}

export default class FieldMappingScreenStore {
  @observable public fieldTypeOverrides = observable.map<
    string,
    PermitFieldType
  >()
  @observable public fieldOrderByStep = observable.map<number, string[]>()
  @observable public captionOverrides = observable.map<string, string>()
  @observable public pendingDeleteFieldId: string = null

  @observable public parentIdOverrides = observable.map<string, string | null>()

  private readonly importedFields: IImportedField[]
  private readonly fieldById: Map<string, IImportedField>
  private readonly originalDto: IPermitType

  public constructor(private readonly draftType: WorkflowType) {
    makeObservable(this)

    this.originalDto = copyObjectDeep<IPermitType>(draftType)
    this.importedFields = this.buildImportedFieldsList()
    this.fieldById = new Map(this.importedFields.map(f => [f.id, f]))
    this.initFieldOrder()
  }

  @computed
  public get allImportedFields(): IImportedField[] {
    return this.importedFields
  }

  @computed
  public get steps(): IWorkflowStep[] {
    return this.draftType.workflowSteps
  }

  @computed
  private get fieldIdToStepIndex(): Map<string, number> {
    const map = new Map<string, number>()
    for (const [stepIndex, ids] of this.fieldOrderByStep.entries()) {
      for (const id of ids) {
        map.set(id, stepIndex)
      }
    }
    return map
  }

  @computed
  public get fieldsByStep(): Map<number, IImportedField[]> {
    const grouped = new Map<number, IImportedField[]>()

    for (const [stepIndex, orderedIds] of this.fieldOrderByStep.entries()) {
      const fieldsInStep = orderedIds
        .map(id => this.fieldById.get(id))
        .filter(Boolean)
      grouped.set(stepIndex, fieldsInStep)
    }

    return grouped
  }

  @computed
  public get assignedPrimaryTypes(): Set<PermitFieldType> {
    const assigned = new Set<PermitFieldType>()
    // Only check fields that are still present in a step (not deleted)
    for (const field of this.importedFields) {
      if (!this.fieldIdToStepIndex.has(field.id)) continue
      const effectiveType = this.getEffectiveType(field.id)
      if (workflowFieldTypes.includes(effectiveType)) {
        assigned.add(effectiveType)
      }
    }
    return assigned
  }

  @computed
  public get hasRequiredFields(): boolean {
    return REQUIRED_FIELD_TYPES.every(type =>
      this.assignedPrimaryTypes.has(type),
    )
  }

  @computed
  public get missingRequiredFieldNames(): string[] {
    return REQUIRED_FIELD_TYPES.filter(
      type => !this.assignedPrimaryTypes.has(type),
    ).map(getFieldTypeCaption)
  }

  @action.bound
  public addMissingRequiredFields(): void {
    const missing = REQUIRED_FIELD_TYPES.filter(
      type => !this.assignedPrimaryTypes.has(type),
    )

    for (const type of missing) {
      const id = BsonObjectId.generate()
      const caption = getFieldTypeCaption(type)

      const field: IImportedField = {
        id,
        caption,
        originalType: type,
        originalParentId: null,
        originalStepIndex: 0,
        stepType: this.draftType.workflowSteps[0]?.type || '',
        fieldIndex: 0,
      }

      this.importedFields.unshift(field)
      this.fieldById.set(id, field)

      const stepOrder = this.fieldOrderByStep.get(0) || []
      this.fieldOrderByStep.set(0, [id, ...stepOrder])
    }
  }

  public getEffectiveType(fieldId: string): PermitFieldType {
    return this.fieldTypeOverrides.get(fieldId) ?? this.getOriginalType(fieldId)
  }

  public getEffectiveCaption(fieldId: string): string {
    return (
      this.captionOverrides.get(fieldId) ??
      this.fieldById.get(fieldId)?.caption ??
      ''
    )
  }

  @action.bound
  public setCaptionOverride(fieldId: string, newCaption: string): void {
    const original = this.fieldById.get(fieldId)?.caption ?? ''
    if (newCaption === original) {
      this.captionOverrides.delete(fieldId)
    } else {
      this.captionOverrides.set(fieldId, newCaption)
    }
  }

  @action.bound
  public showDeleteConfirmation(fieldId: string): void {
    this.pendingDeleteFieldId = fieldId
  }

  @action.bound
  public hideDeleteConfirmation(): void {
    this.pendingDeleteFieldId = null
  }

  @action.bound
  public confirmDeleteField(): void {
    const fieldId = this.pendingDeleteFieldId
    if (!fieldId) return

    this.pendingDeleteFieldId = null
    const stepIndex = this.getEffectiveStepIndex(fieldId)

    // Remove only this field from the order
    const currentOrder = this.fieldOrderByStep.get(stepIndex)
    if (currentOrder) {
      this.fieldOrderByStep.set(
        stepIndex,
        currentOrder.filter(id => id !== fieldId),
      )
    }

    // Reparent direct children to the deleted field's parent
    const deletedParentId = this.getEffectiveParentId(fieldId)
    const orderedIds = this.fieldOrderByStep.get(stepIndex) || []
    for (const id of orderedIds) {
      if (this.getEffectiveParentId(id) === fieldId) {
        this.parentIdOverrides.set(id, deletedParentId)
      }
    }

    // Clean up overrides for the deleted field
    this.fieldTypeOverrides.delete(fieldId)
    this.captionOverrides.delete(fieldId)
    this.parentIdOverrides.delete(fieldId)
  }

  public getEffectiveParentId(fieldId: string): string | null {
    if (this.parentIdOverrides.has(fieldId)) {
      return this.parentIdOverrides.get(fieldId)
    }
    return this.fieldById.get(fieldId)?.originalParentId ?? null
  }

  public getEffectiveStepIndex(fieldId: string): number {
    return (
      this.fieldIdToStepIndex.get(fieldId) ??
      this.fieldById.get(fieldId)?.originalStepIndex ??
      0
    )
  }

  public getDisabledFieldTypes(fieldId: string): PermitFieldType[] {
    return workflowFieldTypes.filter(type => {
      const holder = this.findFieldWithEffectiveType(type)
      return holder && holder.id !== fieldId
    })
  }

  public getAvailableStepsForField(fieldId: string): IStepInfo[] {
    const currentStepIndex = this.getEffectiveStepIndex(fieldId)
    const descendantIds = this.collectDescendantIds(fieldId, currentStepIndex)
    const allIds = [fieldId, ...descendantIds]

    const allTypes = allIds.map(id => this.getEffectiveType(id))
    const hasWorkflowField = allTypes.some(t => workflowFieldTypes.includes(t))

    return this.draftType.workflowSteps.map((step, index) => {
      let disabled = false

      if (hasWorkflowField && index !== 0) {
        disabled = true
      }

      return {
        index,
        type: step.type as WorkflowStepType,
        disabled,
      }
    })
  }

  public treeDataForStep(stepIndex: number): NodeModel<IImportedField>[] {
    const orderedIds = this.fieldOrderByStep.get(stepIndex) || []
    const stepFieldIds = new Set(orderedIds)

    return orderedIds
      .map(id => this.fieldById.get(id))
      .filter(Boolean)
      .map(field => {
        const effectiveParentId = this.getEffectiveParentId(field.id)
        const effectiveType = this.getEffectiveType(field.id)

        // Only use parentId if the parent is in the same step
        const parent =
          effectiveParentId && stepFieldIds.has(effectiveParentId)
            ? effectiveParentId
            : TREE_ROOT_ID

        return {
          id: field.id,
          parent,
          text: field.id,
          droppable: nestablePermitFields.includes(effectiveType),
          data: field,
        }
      })
  }

  public canDropField = (
    tree: NodeModel<IImportedField>[],
    dragSource: NodeModel<IImportedField>,
    dropTargetId: string,
    dropTarget: NodeModel<IImportedField>,
  ): boolean => {
    if (dragSource?.parent === dropTargetId) {
      return true
    }

    if (dropTarget && dragSource?.droppable) {
      const depth = getDepth(tree, dropTarget.id.toString())

      if (depth > MAX_FIELDS_DEPTH) {
        return false
      }
      const childrenDepth = getChildrenDepth(tree, [dragSource.id.toString()])
      if (childrenDepth + depth > MAX_FIELDS_DEPTH) {
        return false
      }
    }
  }

  @action.bound
  public reassignFieldType(fieldId: string, newType: PermitFieldType): void {
    if (workflowFieldTypes.includes(newType)) {
      const currentHolder = this.findFieldWithEffectiveType(newType)
      if (currentHolder && currentHolder.id !== fieldId) {
        this.fieldTypeOverrides.set(currentHolder.id, PermitFieldType.InputText)
      }
    }

    const originalType = this.getOriginalType(fieldId)
    if (newType === originalType) {
      this.fieldTypeOverrides.delete(fieldId)
    } else {
      this.fieldTypeOverrides.set(fieldId, newType)
    }

    // Auto-move first-step-only types into step 0
    if (workflowFieldTypes.includes(newType)) {
      const currentStep = this.getEffectiveStepIndex(fieldId)
      if (currentStep !== 0) {
        this.moveFieldToStep(fieldId, 0)
      }
    }
  }

  @action.bound
  public onTreeDrop(
    stepIndex: number,
    newTreeData: NodeModel<IImportedField>[],
  ): void {
    const sortedData: NodeModel<IImportedField>[] = []
    sortTreeData(newTreeData, sortedData, TREE_ROOT_ID)

    // Update field order
    const newOrder = sortedData.map(node => node.id.toString())
    this.fieldOrderByStep.set(stepIndex, newOrder)

    // Update parentId overrides
    for (const node of sortedData) {
      const fieldId = node.id.toString()
      const newParentId =
        node.parent === TREE_ROOT_ID ? null : node.parent.toString()
      const originalParentId =
        this.fieldById.get(fieldId)?.originalParentId ?? null

      if (newParentId === originalParentId) {
        this.parentIdOverrides.delete(fieldId)
      } else {
        this.parentIdOverrides.set(fieldId, newParentId)
      }
    }
  }

  @action.bound
  public moveFieldToStep(fieldId: string, targetStepIndex: number): void {
    const currentStepIndex = this.getEffectiveStepIndex(fieldId)
    if (currentStepIndex === targetStepIndex) return

    // Collect the field and all its descendants
    const allDescendantIds = this.collectDescendantIds(
      fieldId,
      currentStepIndex,
    )
    allDescendantIds.unshift(fieldId)

    // Separate descendants into those that can move and those that must stay
    const idsToMove: string[] = []
    const idsToLeave: string[] = []
    const parentOfMovedField = this.getEffectiveParentId(fieldId)

    for (const id of allDescendantIds) {
      const type = this.getEffectiveType(id)
      const isWorkflowField = workflowFieldTypes.includes(type)

      const blocked = isWorkflowField && targetStepIndex !== 0

      if (blocked && id !== fieldId) {
        idsToLeave.push(id)
      } else {
        idsToMove.push(id)
      }
    }

    // Reparent left-behind fields to the moved field's original parent
    for (const id of idsToLeave) {
      const directParent = this.getEffectiveParentId(id)
      // If the direct parent is being moved, reparent to the root-level parent
      if (directParent && idsToMove.includes(directParent)) {
        this.parentIdOverrides.set(id, parentOfMovedField)
      }
    }

    const moveSet = new Set(idsToMove)
    const sourceOrder = this.fieldOrderByStep.get(currentStepIndex)
    if (sourceOrder) {
      this.fieldOrderByStep.set(
        currentStepIndex,
        sourceOrder.filter(id => !moveSet.has(id)),
      )
    }

    const targetOrder = this.fieldOrderByStep.get(targetStepIndex) || []
    this.fieldOrderByStep.set(targetStepIndex, [...targetOrder, ...idsToMove])
  }

  /**
   * Builds a new PermitType from the plain DTO snapshot, reflecting all
   * current overrides, reordering, and step transfers. Used by autorun
   * for the preview panel. Reads observable maps so MobX tracks dependencies.
   */
  public buildCurrentPermitType(): WorkflowType {
    const dto = copyObjectDeep<IPermitType>(this.originalDto)
    this.applyToSteps(dto.workflowSteps)
    return WorkflowType.fromDto(dto)
  }

  /**
   * Directly mutates the draftType's observable workflowSteps to reflect
   * all overrides, reordering, and step transfers. Call before transitioning
   * to FormTypeConfigurator so the existing MobX observable has the correct
   * state for getDeepCopy() to serialize.
   */
  @action.bound
  public applyChangesToDraft(): void {
    this.applyToSteps(this.draftType.workflowSteps)
  }

  private applyToSteps(steps: IWorkflowStep[]): void {
    // Collect all fields by ID across all steps
    const allFieldsMap = new Map<string, IPermitTypeField>()
    for (const step of steps) {
      if (step.conditionalFields) {
        step.conditionalFields = step.conditionalFields.map(conditional => ({
          ...conditional,
          values: conditional.values?.map(value =>
            value ? withResourceCreationOff(value) : value,
          ),
        }))
      }
      if (!step.fields) continue
      for (const field of step.fields) {
        if (!field) continue
        allFieldsMap.set(field.id, withResourceCreationOff(field))
      }
    }

    // Add newly created fields (from addMissingRequiredFields) that
    // don't exist in the original steps yet
    for (const field of this.importedFields) {
      if (!allFieldsMap.has(field.id)) {
        allFieldsMap.set(field.id, {
          id: field.id,
          caption: field.caption,
          type: field.originalType,
          parentId: field.originalParentId,
          isMandatory: false,
          isShown: true,
          canEditIsMandatory: true,
          canEditIsShown: true,
          filterOptions: getDefaultFilterOptions(field.originalType),
          allowCreateNew: getDefaultAllowCreateNew(field.originalType),
          isLBSLogicEnabled: false,
        } as IPermitTypeField)
      }
    }

    // Apply type overrides
    for (const [fieldId, newType] of this.fieldTypeOverrides.entries()) {
      const field = allFieldsMap.get(fieldId)
      if (field) {
        allFieldsMap.set(
          fieldId,
          withResourceCreationOff({
            ...field,
            type: newType,
            filterOptions: getDefaultFilterOptions(newType),
            allowCreateNew: getDefaultAllowCreateNew(newType),
          }),
        )
      }
    }

    // Apply caption overrides
    for (const [fieldId, newCaption] of this.captionOverrides.entries()) {
      const field = allFieldsMap.get(fieldId)
      if (field) {
        allFieldsMap.set(fieldId, { ...field, caption: newCaption })
      }
    }

    // Apply parentId overrides
    for (const [fieldId, newParentId] of this.parentIdOverrides.entries()) {
      const field = allFieldsMap.get(fieldId)
      if (field) {
        allFieldsMap.set(fieldId, { ...field, parentId: newParentId })
      }
    }

    // Rebuild each step's fields from the order map
    for (let stepIdx = 0; stepIdx < steps.length; stepIdx++) {
      const orderedIds = this.fieldOrderByStep.get(stepIdx)
      if (orderedIds) {
        const fields = orderedIds
          .map(id => allFieldsMap.get(id))
          .filter(Boolean)

        // Clear parentId for fields whose parent is not in the same step,
        // otherwise the tree renderer can't find the parent and hides them
        const stepFieldIds = new Set(fields.map(f => f.id))
        steps[stepIdx].fields = fields.map(f =>
          f.parentId && !stepFieldIds.has(f.parentId)
            ? { ...f, parentId: null }
            : f,
        )
      }
    }
  }

  private collectDescendantIds(parentId: string, stepIndex: number): string[] {
    const descendants: string[] = []
    const orderedIds = this.fieldOrderByStep.get(stepIndex) || []

    for (const id of orderedIds) {
      if (this.getEffectiveParentId(id) === parentId) {
        descendants.push(id)
        descendants.push(...this.collectDescendantIds(id, stepIndex))
      }
    }

    return descendants
  }

  private getOriginalType(fieldId: string): PermitFieldType {
    return (
      this.fieldById.get(fieldId)?.originalType ?? PermitFieldType.InputText
    )
  }

  private findFieldWithEffectiveType(
    type: PermitFieldType,
  ): IImportedField | null {
    return (
      this.importedFields.find(
        f =>
          this.fieldIdToStepIndex.has(f.id) &&
          this.getEffectiveType(f.id) === type,
      ) ?? null
    )
  }

  private initFieldOrder(): void {
    const grouped = new Map<number, string[]>()
    for (const field of this.importedFields) {
      const existing = grouped.get(field.originalStepIndex) || []
      existing.push(field.id)
      grouped.set(field.originalStepIndex, existing)
    }
    for (let i = 0; i < this.draftType.workflowSteps.length; i++) {
      this.fieldOrderByStep.set(i, grouped.get(i) || [])
    }
  }

  private buildImportedFieldsList(): IImportedField[] {
    const fields: IImportedField[] = []

    this.draftType.workflowSteps.forEach(
      (step: IWorkflowStep, stepIndex: number) => {
        if (!step.fields) return

        step.fields.forEach((field: IPermitTypeField, fieldIndex: number) => {
          if (!field || !field.type) return

          fields.push({
            id: field.id,
            caption: field.caption || `Field ${fieldIndex + 1}`,
            originalType: field.type,
            originalParentId: field.parentId || null,
            originalStepIndex: stepIndex,
            stepType: step.type,
            fieldIndex,
          })
        })
      },
    )

    return fields
  }
}
